import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { Award, Trash2, Loader2, ImagePlus, Leaf } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SUSTAINABILITY_QUESTIONS = [
  { id: 'waste_segregation', category: 'Waste Management', q: 'Do you separate wet and dry waste on your property?', proof: 'Photo of separate bins' },
  { id: 'avoid_plastic', category: 'Waste Management', q: 'Do you avoid single-use plastic bottles, cups, plates, or straws for guests?', proof: 'Photo of reusable items' },
  { id: 'led_energy', category: 'Energy Efficiency', q: 'Do you use LED bulbs or any electricity-saving measures?', proof: 'Photo' },
  { id: 'water_conservation', category: 'Water Conservation', q: 'Do you save water (rainwater harvesting, storage, controlled usage)?', proof: 'Photo' },
  { id: 'local_employment', category: 'Local Employment', q: 'Do you employ local people for housekeeping, cooking, guiding, driving, or other services?', proof: 'Staff details / photo' },
  { id: 'local_vendors', category: 'Local Sourcing', q: 'Do you buy food, vegetables, handicrafts, or other products from local vendors?', proof: 'Bills / photo' },
  { id: 'grown_on_property', category: 'Local Sourcing', q: 'Do you grow fruits, vegetables, herbs, or other products on your farm/property?', proof: 'Photo' },
  { id: 'local_food', category: 'Local Sourcing', q: 'Do you serve local or traditional food to guests?', proof: 'Menu / photo' },
  { id: 'cultural_experiences', category: 'Cultural & Community', q: 'Do you offer guests local cultural experiences, village activities, crafts, or traditions?', proof: 'Photos / activity details' },
  { id: 'responsible_behaviour', category: 'Cultural & Community', q: 'Do you help guests understand local customs and encourage responsible behaviour towards nature & community?', proof: 'Signage, guidebook, or photo' },
];

export const tierFromScore = (score) => {
  if (score >= 8) return 'gold';
  if (score >= 4) return 'silver';
  if (score >= 1) return 'bronze';
  return null;
};

/**
 * Validate sustainability checklist: for every YES answer, a proof photo must be attached.
 * Returns null if valid, or an error string if invalid.
 */
export const validateSustainability = (value) => {
  if (!value || !value.answers) return null;
  const proofs = value.proof_photos || {};
  const missing = [];
  for (const [id, checked] of Object.entries(value.answers)) {
    if (checked === true && !proofs[id]) {
      const q = SUSTAINABILITY_QUESTIONS.find((qq) => qq.id === id);
      missing.push(q ? q.q : id);
    }
  }
  if (missing.length) {
    return `Please upload a proof photo for each checked item. Missing: "${missing[0]}"${missing.length > 1 ? ` and ${missing.length - 1} more` : ''}.`;
  }
  return null;
};

export const TIER_LABEL = {
  gold: '🥇 Gold Sustainable Host',
  silver: '🥈 Silver Sustainable Host',
  bronze: '🥉 Bronze Sustainable Host',
};

const SustainabilityChecklist = ({ value, onChange }) => {
  const answers = value?.answers || {};
  const proofs = value?.proof_photos || {};
  const fileInputs = useRef({});
  const [uploadingId, setUploadingId] = useState(null);

  const score = Object.values(answers).filter((v) => v === true).length;
  const tier = tierFromScore(score);

  const setAnswer = (id, v) => {
    onChange({ ...value, answers: { ...answers, [id]: v } });
  };
  const setProof = (id, url) => {
    onChange({ ...value, proof_photos: { ...proofs, [id]: url } });
  };

  const handleUpload = async (id, file) => {
    if (!file || !file.type?.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/properties/upload-room-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.success && res.data?.url) {
        setProof(id, res.data.url);
        toast.success('Proof uploaded');
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingId(null);
      if (fileInputs.current[id]) fileInputs.current[id].value = '';
    }
  };

  // Group questions by category
  const grouped = SUSTAINABILITY_QUESTIONS.reduce((acc, q) => {
    (acc[q.category] = acc[q.category] || []).push(q);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="sustainability-checklist">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
        <Leaf className="h-5 w-5 text-green-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-semibold text-green-900">Sustainability Self-Assessment (optional)</p>
            {tier && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white border border-green-300 text-green-800" data-testid="sustainability-self-tier">
                {TIER_LABEL[tier]} · {score}/10
              </span>
            )}
          </div>
          <p className="text-xs text-green-800 mt-1">
            Tick each practice you follow and upload a proof photo. After admin verification, your property gets a sustainability badge on its listing.
            <br />
            <span className="font-medium">Tiers:</span> 1-3 YES → Bronze 🥉 · 4-7 YES → Silver 🥈 · 8-10 YES → Gold 🥇
          </p>
        </div>
      </div>

      {Object.entries(grouped).map(([category, qs]) => (
        <div key={category} className="border rounded-lg p-3 space-y-2">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{category}</p>
          {qs.map((q) => {
            const checked = answers[q.id] === true;
            const proofUrl = proofs[q.id];
            return (
              <div key={q.id} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 py-2 border-t first:border-t-0">
                <Checkbox
                  id={`sust-${q.id}`}
                  checked={checked}
                  onCheckedChange={(v) => setAnswer(q.id, !!v)}
                  className="mt-0.5"
                  data-testid={`sust-check-${q.id}`}
                />
                <div className="flex-1 min-w-0">
                  <Label htmlFor={`sust-${q.id}`} className="text-sm cursor-pointer leading-snug">
                    {q.q}
                  </Label>
                  {checked && (
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <span className={`text-[11px] ${proofUrl ? 'text-gray-500' : 'text-red-600 font-semibold'}`}>
                        {proofUrl ? `Proof: ${q.proof}` : `⚠ Proof required: ${q.proof}`}
                      </span>
                      <input
                        ref={(el) => { fileInputs.current[q.id] = el; }}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleUpload(q.id, e.target.files?.[0])}
                        className="hidden"
                      />
                      {proofUrl ? (
                        <>
                          <a href={proofUrl.startsWith('http') ? proofUrl : `${BACKEND_URL}${proofUrl}`} target="_blank" rel="noreferrer" className="text-xs text-[#7CB342] hover:underline" data-testid={`sust-proof-link-${q.id}`}>
                            View
                          </a>
                          <button type="button" onClick={() => setProof(q.id, '')} className="text-red-500 hover:text-red-700" data-testid={`sust-proof-remove-${q.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => fileInputs.current[q.id]?.click()}
                          disabled={uploadingId === q.id}
                          className="h-7 px-2 text-xs"
                          data-testid={`sust-proof-upload-${q.id}`}
                        >
                          {uploadingId === q.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImagePlus className="h-3 w-3" />}
                          <span className="ml-1">Upload</span>
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default SustainabilityChecklist;
